import type { EditKind } from "./graduation";

/**
 * TA-187, Agent Brains section 5.9 (the Learning agent). Charter `produces`
 * line, verbatim: "Pattern proposals with citations, and graduation
 * recommendations." `graduationRecord.ts` is the second half; this file is
 * the first.
 *
 * WHAT THIS IS NOT. `packages/pipeline/src/stages/stage4.ts` already mines
 * `PlaybookPattern`s (`packages/agents/src/playcard.ts`) out of the OFFLINE
 * corpus, a completely different pipeline this task does not touch: those
 * patterns come from a batch run over historical or synthetic threads, gate
 * on `PATTERN_MIN_SAMPLE` and an externally-supplied `ratified` set at
 * render time (`buildPlaycard`), a set the owner populates by ratifying
 * (`packages/db/src/patternRatifications.ts`, read per compose run by the
 * worker's reply graph since L13). This file reads a different source
 * entirely: the charter's own handoff line, "Reads the ledger", which in this
 * codebase means real decided `drafts` rows and the `facts_used` every one of
 * them already carries under section 4's mandatory grounding output. Building
 * a second pattern miner over the offline corpus would duplicate stage4.ts
 * for no reason, and wiring `ratifiedPatterns` was the Playbook pipeline's
 * gap, not this charter's "Reads the ledger" line (FINDINGS.md TA-187, closed
 * by L13).
 *
 * THE GROUPING KEY, AND WHY IT IS NOT INVENTED. "What counts as the same
 * pattern" is a genuine modeling question CLAUDE.md section 12 says not to
 * silently decide. This file answers it the narrowest way that needed no
 * invention at all: two approved-without-edit drafts from the SAME agent
 * that cite the EXACT SAME SET of KB facts (`facts_used`, section 4's own
 * mandatory grounding output, already required on every draft) are the same
 * shape. No text similarity, no embedding distance, no keyword heuristic:
 * every input this groups on is already a precise, required field. Order
 * within the set does not matter (two drafts pulling the same two facts in
 * a different retrieval order are still answering the same question), so
 * the fingerprint sorts before joining. This is a real, defensible reading,
 * not the only possible one, and is logged as such in FINDINGS.md TA-187
 * for Kabir to confirm or replace.
 *
 * NEVER RATIFIES ANYTHING. F-15 (folded-in law): single-client patterns
 * ship "owner-ratified provisional", n>=5 consistent threads AND owner
 * sign-off. `PatternCandidateStatus` supplies only the n>=5 half; there is
 * no code path in this file from "sample size cleared" to "ratified" or
 * "shipped", because the owner sign-off half is not something a function
 * reading a database can grant itself. A human reads `citations` and
 * decides, exactly like `playcard.ts`'s own `ratified` set already requires
 * for the offline pipeline's patterns.
 */

/**
 * F-15's own n>=5 bar (CLAUDE.md section 13, ratified, not open). This is
 * the THIRD copy of the same number in this codebase:
 * `packages/agents/src/playcard.ts`'s `PATTERN_MIN_SAMPLE` already
 * duplicates `packages/core/src/retroGrill.ts`'s
 * `RETRO_GRILL_WIN_MIN_SAMPLE`, both because `core` cannot depend on
 * `agents` (the dependency runs the other way). Not marked AWAITING
 * RATIFICATION: the number itself is settled law, F-15. Since TA-239,
 * `packages/agents/src/constantsSync.test.ts` holds all three copies equal.
 * It proves agreement, not the value, and a fourth copy or an inlined
 * literal would slip past it. Whether to de-duplicate instead stays open.
 */
export const PATTERN_CANDIDATE_MIN_SAMPLE = 5;

/**
 * How many citations a proposal shows for reading. Independent of, but the
 * same value and the same reasoning as, `graduationRecord.ts`'s
 * `GRADUATION_SAMPLE_CAP` and `outcomeDigest.ts`'s `DIGEST_LIST_CAP`: a
 * short reading list plus an honest, never-capped total (`citationsOverflow`,
 * `sampleSize`). AWAITING RATIFICATION.
 */
export const PATTERN_CITATION_CAP = 10;

/**
 * Never "ratified", "shipped" or "active": those states belong to a human
 * decision this file cannot make for itself. `insufficient_sample` is the
 * honest state a real repeat shape gets before F-15's bar;
 * `candidate_pending_owner_signoff` is what it gets after, and still
 * requires the human half the name says.
 */
export type PatternCandidateStatus = "insufficient_sample" | "candidate_pending_owner_signoff";

export interface PatternInputRow {
  readonly draftId: string;
  readonly agentId: string;
  readonly threadId: string;
  /** Section 4's mandatory grounding output: the KB fact ids this reply
   * actually cited. */
  readonly factsUsed: readonly string[];
  readonly status: "approved" | "rejected";
  readonly editKind: EditKind | null;
  readonly decidedAt: Date;
}

export interface PatternCitation {
  readonly draftId: string;
  readonly threadId: string;
}

export interface PatternProposal {
  readonly agentId: string;
  /** Canonical (sorted) fact-id set this proposal is grouped on. */
  readonly factsUsed: readonly string[];
  readonly fingerprint: string;
  /** Every approved-without-edit draft with this exact shape, never capped. */
  readonly sampleSize: number;
  readonly status: PatternCandidateStatus;
  /** Most recent first, capped at `PATTERN_CITATION_CAP`. */
  readonly citations: readonly PatternCitation[];
  /** What the citation cap hid. Zero when every instance is already shown. */
  readonly citationsOverflow: number;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
}

export interface PatternSurfaceResult {
  readonly proposals: readonly PatternProposal[];
  /** Every row handed in. `proposals[*].sampleSize` summed, plus both
   * exclusion counts below, always equals this: nothing is silently
   * dropped, only counted under a different bucket. */
  readonly totalRowsConsidered: number;
  /** Rejected, or approved with a substantive edit (F-05): not "what
   * worked unedited". */
  readonly excludedNotApprovedWithoutEdit: number;
  /** Approved without edit but cited no KB fact: nothing to cite. */
  readonly excludedNoFacts: number;
}

export interface SurfacePatternCandidatesOptions {
  readonly minSample?: number;
  readonly citationCap?: number;
}

interface Group {
  readonly agentId: string;
  readonly factsUsed: readonly string[];
  readonly rows: PatternInputRow[];
}

/**
 * Groups real, approved-without-edit drafts by (agent, exact fact-set) and
 * reports each group as a `PatternProposal`. Pure: reads only what it is
 * handed, never queries the corpus, the Playbook pipeline, or anything
 * synthetic. NEVER marks a proposal ratified, shipped or active.
 */
export function surfacePatternCandidates(
  rows: readonly PatternInputRow[],
  opts: SurfacePatternCandidatesOptions = {},
): PatternSurfaceResult {
  const minSample = opts.minSample ?? PATTERN_CANDIDATE_MIN_SAMPLE;
  const citationCap = opts.citationCap ?? PATTERN_CITATION_CAP;

  let excludedNotApprovedWithoutEdit = 0;
  let excludedNoFacts = 0;
  const groups = new Map<string, Group>();

  for (const r of rows) {
    const approvedWithoutEdit =
      r.status === "approved" && r.editKind !== null && r.editKind !== "substantive";
    if (!approvedWithoutEdit) {
      excludedNotApprovedWithoutEdit += 1;
      continue;
    }
    if (r.factsUsed.length === 0) {
      excludedNoFacts += 1;
      continue;
    }
    const canonicalFacts = [...r.factsUsed].sort();
    const fingerprint = `${r.agentId}::${canonicalFacts.join("|")}`;
    let g = groups.get(fingerprint);
    if (!g) {
      g = { agentId: r.agentId, factsUsed: canonicalFacts, rows: [] };
      groups.set(fingerprint, g);
    }
    g.rows.push(r);
  }

  const proposals: PatternProposal[] = [];
  for (const [fingerprint, g] of groups) {
    const byRecency = [...g.rows].sort((a, b) => b.decidedAt.getTime() - a.decidedAt.getTime());
    const sampleSize = byRecency.length;
    const times = g.rows.map((r) => r.decidedAt.getTime());
    proposals.push({
      agentId: g.agentId,
      factsUsed: g.factsUsed,
      fingerprint,
      sampleSize,
      status: sampleSize >= minSample ? "candidate_pending_owner_signoff" : "insufficient_sample",
      citations: byRecency
        .slice(0, citationCap)
        .map((r) => ({ draftId: r.draftId, threadId: r.threadId })),
      citationsOverflow: Math.max(0, sampleSize - citationCap),
      firstSeenAt: new Date(Math.min(...times)).toISOString(),
      lastSeenAt: new Date(Math.max(...times)).toISOString(),
    });
  }

  // Deterministic order: the most-supported shape first, fingerprint breaks
  // ties, so re-running this over the same rows never reorders two
  // equal-sample proposals depending on read order.
  proposals.sort(
    (a, b) => b.sampleSize - a.sampleSize || a.fingerprint.localeCompare(b.fingerprint),
  );

  return {
    proposals,
    totalRowsConsidered: rows.length,
    excludedNotApprovedWithoutEdit,
    excludedNoFacts,
  };
}
